import { lazy, type ComponentType } from "react";

const CHUNK_RELOAD_KEY = "chunk_reload";

/** Cleared on successful app boot so a future deploy can trigger one auto-reload again. */
export function clearChunkReloadFlag() {
  sessionStorage.removeItem(CHUNK_RELOAD_KEY);
}

export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const message = error.message.toLowerCase();
  return (
    message.includes("failed to fetch dynamically imported module") ||
    message.includes("importing a module script failed") ||
    message.includes("error loading dynamically imported module")
  );
}

/**
 * Wraps React.lazy dynamic imports to handle stale chunk errors after deployments.
 * On chunk load failure, reloads the page once to fetch fresh HTML with new chunk references.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(() =>
    factory().catch((error) => {
      if (!isChunkLoadError(error)) {
        throw error;
      }

      const hasReloaded = sessionStorage.getItem(CHUNK_RELOAD_KEY);
      if (!hasReloaded) {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
        window.location.replace(window.location.href);
        // Return a never-resolving promise to prevent rendering while reloading
        return new Promise(() => {});
      }

      sessionStorage.removeItem(CHUNK_RELOAD_KEY);
      throw error;
    }),
  );
}
